/**
 * Persistência IndexedDB do catálogo ERP (produtos, grupos, marcas, unidades).
 * Substitui o blob monolítico em localStorage.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export interface ErpCachedProduct {
  id: number;
  sku?: string | null;
  barcode?: string | null;
  name: string;
  nameNormalized?: string;
  description?: string | null;
  cost_price?: number | null;
  price: number;
  stock_quantity?: number;
  min_stock?: number | null;
  max_stock?: number | null;
  active?: boolean;
  group_id?: number | null;
  brand_id?: number | null;
  unit_id?: number | null;
  brand_name?: string | null;
  group_name?: string | null;
  unit_name?: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface ErpCachedGroup {
  id: number;
  name: string;
  code?: string;
  description?: string;
  parent_id?: number;
  active: boolean;
  [key: string]: unknown;
}

export interface ErpCachedBrand {
  id: number;
  name: string;
  code?: string;
  active: boolean;
  [key: string]: unknown;
}

export interface ErpCachedUnit {
  id: number;
  name: string;
  code?: string;
  active: boolean;
  [key: string]: unknown;
}

export type MetaKey = 'products' | 'groups' | 'brands' | 'units';

export interface CacheMeta {
  key: MetaKey;
  syncedAt: number;
  version: number;
  count: number;
}

interface ErpCacheDb extends DBSchema {
  products: {
    key: number;
    value: ErpCachedProduct;
    indexes: {
      sku: string;
      barcode: string;
      nameNormalized: string;
    };
  };
  groups: {
    key: number;
    value: ErpCachedGroup;
  };
  brands: {
    key: number;
    value: ErpCachedBrand;
  };
  units: {
    key: number;
    value: ErpCachedUnit;
  };
  meta: {
    key: MetaKey;
    value: CacheMeta;
  };
}

const DB_NAME = 'wfmed_erp_cache';
const DB_VERSION = 1;

/** Chaves legadas do localStorage (migração one-shot). */
export const LEGACY_LS_KEYS = {
  products: 'wfmed_erp_products',
  groups: 'wfmed_erp_product_groups',
  brands: 'wfmed_erp_brands',
  units: 'wfmed_erp_units',
  productsAlt: 'erp_products_cache',
  groupsAlt: 'erp_groups_cache',
  brandsAlt: 'erp_brands_cache',
  unitsAlt: 'erp_units_cache',
} as const;

let dbPromise: Promise<IDBPDatabase<ErpCacheDb>> | null = null;
let migrationDone = false;

function normalizeName(name: string | undefined | null): string {
  return String(name || '').toLowerCase().trim();
}

export function withSearchFields<T extends { id: number; name?: string; sku?: string | null; barcode?: string | null }>(
  product: T
): T & { nameNormalized: string; sku: string; barcode: string } {
  return {
    ...product,
    nameNormalized: normalizeName(product.name),
    sku: product.sku != null ? String(product.sku) : '',
    barcode: product.barcode != null ? String(product.barcode) : '',
  };
}

export async function getErpCacheDb(): Promise<IDBPDatabase<ErpCacheDb>> {
  if (!dbPromise) {
    dbPromise = openDB<ErpCacheDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('products')) {
          const store = db.createObjectStore('products', { keyPath: 'id' });
          store.createIndex('sku', 'sku');
          store.createIndex('barcode', 'barcode');
          store.createIndex('nameNormalized', 'nameNormalized');
        }
        if (!db.objectStoreNames.contains('groups')) {
          db.createObjectStore('groups', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('brands')) {
          db.createObjectStore('brands', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('units')) {
          db.createObjectStore('units', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      },
    });
  }
  const db = await dbPromise;
  if (!migrationDone) {
    migrationDone = true;
    await migrateFromLocalStorage(db).catch((err) => {
      console.warn('[erpProductCacheDb] Migração localStorage falhou:', err);
    });
  }
  return db;
}

function readLegacyArray(key: string): { data: unknown[]; timestamp: number } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { data: parsed, timestamp: Date.now() };
    }
    if (parsed && Array.isArray(parsed.data)) {
      return {
        data: parsed.data,
        timestamp: typeof parsed.timestamp === 'number' ? parsed.timestamp : Date.now(),
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

async function migrateFromLocalStorage(db: IDBPDatabase<ErpCacheDb>): Promise<void> {
  const existingMeta = await db.get('meta', 'products');
  if (existingMeta && existingMeta.count > 0) {
    // Já tem IDB populado — só limpa chaves legadas se existirem
    clearLegacyKeys();
    return;
  }

  const productsLegacy =
    readLegacyArray(LEGACY_LS_KEYS.products) ||
    readLegacyArray(LEGACY_LS_KEYS.productsAlt);

  if (productsLegacy?.data?.length) {
    const products = (productsLegacy.data as ErpCachedProduct[]).map((p) =>
      withSearchFields(p)
    );
    const tx = db.transaction('products', 'readwrite');
    await Promise.all([
      ...products.map((p) => tx.store.put(p)),
      tx.done,
    ]);
    await db.put('meta', {
      key: 'products',
      syncedAt: productsLegacy.timestamp,
      version: 1,
      count: products.length,
    });
  }

  await migrateListStore(db, 'groups', [
    LEGACY_LS_KEYS.groups,
    LEGACY_LS_KEYS.groupsAlt,
  ]);
  await migrateListStore(db, 'brands', [
    LEGACY_LS_KEYS.brands,
    LEGACY_LS_KEYS.brandsAlt,
  ]);
  await migrateListStore(db, 'units', [
    LEGACY_LS_KEYS.units,
    LEGACY_LS_KEYS.unitsAlt,
  ]);

  clearLegacyKeys();
}

async function migrateListStore(
  db: IDBPDatabase<ErpCacheDb>,
  store: 'groups' | 'brands' | 'units',
  keys: string[]
): Promise<void> {
  const existing = await db.get('meta', store);
  if (existing && existing.count > 0) return;

  let legacy: { data: unknown[]; timestamp: number } | null = null;
  for (const key of keys) {
    legacy = readLegacyArray(key);
    if (legacy?.data?.length) break;
  }
  if (!legacy?.data?.length) return;

  const items = legacy.data as Array<{ id: number }>;
  const tx = db.transaction(store, 'readwrite');
  await Promise.all([...items.map((item) => tx.store.put(item as never)), tx.done]);
  await db.put('meta', {
    key: store,
    syncedAt: legacy.timestamp,
    version: 1,
    count: items.length,
  });
}

function clearLegacyKeys(): void {
  Object.values(LEGACY_LS_KEYS).forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
}

export async function getMeta(key: MetaKey): Promise<CacheMeta | undefined> {
  const db = await getErpCacheDb();
  return db.get('meta', key);
}

export async function setMeta(
  key: MetaKey,
  syncedAt: number,
  count: number
): Promise<void> {
  const db = await getErpCacheDb();
  await db.put('meta', { key, syncedAt, version: 1, count });
}

export async function putAllProducts(products: ErpCachedProduct[]): Promise<void> {
  const db = await getErpCacheDb();
  const prepared = products.map((p) => withSearchFields(p));
  const tx = db.transaction('products', 'readwrite');
  await tx.store.clear();
  await Promise.all([...prepared.map((p) => tx.store.put(p)), tx.done]);
  await setMeta('products', Date.now(), prepared.length);
}

export async function getAllProducts(): Promise<ErpCachedProduct[]> {
  const db = await getErpCacheDb();
  return db.getAll('products');
}

export async function upsertProduct(product: ErpCachedProduct): Promise<void> {
  const db = await getErpCacheDb();
  await db.put('products', withSearchFields(product));
  const count = await db.count('products');
  const meta = await getMeta('products');
  await setMeta('products', meta?.syncedAt || Date.now(), count);
}

export async function deleteProduct(productId: number): Promise<void> {
  const db = await getErpCacheDb();
  await db.delete('products', productId);
  const count = await db.count('products');
  const meta = await getMeta('products');
  await setMeta('products', meta?.syncedAt || Date.now(), count);
}

export async function clearProducts(): Promise<void> {
  const db = await getErpCacheDb();
  await db.clear('products');
  await db.delete('meta', 'products');
}

async function putAllList<T extends { id: number }>(
  store: 'groups' | 'brands' | 'units',
  items: T[]
): Promise<void> {
  const db = await getErpCacheDb();
  const tx = db.transaction(store, 'readwrite');
  await tx.store.clear();
  await Promise.all([...items.map((item) => tx.store.put(item as never)), tx.done]);
  await setMeta(store, Date.now(), items.length);
}

async function getAllList<T>(store: 'groups' | 'brands' | 'units'): Promise<T[]> {
  const db = await getErpCacheDb();
  return db.getAll(store) as Promise<T[]>;
}

async function clearList(store: 'groups' | 'brands' | 'units'): Promise<void> {
  const db = await getErpCacheDb();
  await db.clear(store);
  await db.delete('meta', store);
}

export const putAllGroups = (items: ErpCachedGroup[]) => putAllList('groups', items);
export const getAllGroups = () => getAllList<ErpCachedGroup>('groups');
export const clearGroups = () => clearList('groups');

export const putAllBrands = (items: ErpCachedBrand[]) => putAllList('brands', items);
export const getAllBrands = () => getAllList<ErpCachedBrand>('brands');
export const clearBrands = () => clearList('brands');

export const putAllUnits = (items: ErpCachedUnit[]) => putAllList('units', items);
export const getAllUnits = () => getAllList<ErpCachedUnit>('units');
export const clearUnits = () => clearList('units');

export async function clearAllErpCaches(): Promise<void> {
  await Promise.all([
    clearProducts(),
    clearGroups(),
    clearBrands(),
    clearUnits(),
  ]);
}

export function isBrowserOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

export function filterProductsLocal(
  products: ErpCachedProduct[],
  term: string
): ErpCachedProduct[] {
  const q = String(term || '').toLowerCase().trim();
  if (!q) return products;
  return products.filter(
    (p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.barcode || '').toLowerCase().includes(q) ||
      (p.nameNormalized || '').includes(q)
  );
}
